/**
 * 一斉送信「顧客を指定」モードで使う複数選択ピッカー。
 *
 * - 検索: slash-app `/api/auth/customers/?q=` を 250ms デバウンスで叩く
 * - 選択: クリックで追加/解除。選択済みは別エリアにチップ表示
 * - 上限: ``CUSTOM_TARGET_LIMIT`` (= 1000)。超過時は追加ボタン無効化＋警告
 * - 返り値: 親に伝えるのは selected の ``id`` 配列 (= auth_user.id)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Header,
  Input,
  Loader,
  Message,
  Segment,
  Icon,
  Label,
} from "semantic-ui-react";
import { slashAppBroadcasts } from "../utils/slashAppBroadcasts";

// サーバ側 (apps/broadcasts/services.CUSTOM_TARGET_LIMIT) と揃える。
export const CUSTOM_TARGET_LIMIT = 1000;

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 50;

function CustomerPicker({ value, onChange }) {
  // value: number[] (auth_user.id)
  // 表示用に id だけでなく label も保持したいので「選択済み顧客」を別途持つ。
  const [selectedItems, setSelectedItems] = useState([]); // [{id, display_name, kana, ...}]
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const debounceTimer = useRef(null);

  // 親から渡された value (id配列) と現在の selectedItems を同期。
  // value にあるけど selectedItems に無いものは表示できないので、
  // 親が初期値を渡すケース (編集モードなど) では別途プリロードが必要だが、
  // 今は新規作成のみ対応なので、value=[] が前提。
  useEffect(() => {
    setSelectedItems((prev) =>
      prev.filter((it) => (value || []).includes(it.id))
    );
  }, [value]);

  const runSearch = useCallback(async (q) => {
    setSearching(true);
    setSearchError(null);
    try {
      const res = await slashAppBroadcasts.searchCustomers({
        q,
        limit: PAGE_SIZE,
      });
      setResults(res.results || []);
    } catch (e) {
      setSearchError(e.message || "顧客検索に失敗しました");
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // デバウンス検索。
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const q = query.trim();
    if (!q) {
      setResults([]);
      setSearchError(null);
      return;
    }
    debounceTimer.current = setTimeout(() => runSearch(q), DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [query, runSearch]);

  const isSelected = useCallback(
    (id) => (value || []).includes(id),
    [value]
  );

  const addOne = useCallback(
    (item) => {
      const next = value || [];
      if (next.includes(item.id)) return;
      if (next.length >= CUSTOM_TARGET_LIMIT) return;
      setSelectedItems((prev) =>
        prev.some((p) => p.id === item.id) ? prev : [...prev, item]
      );
      onChange([...next, item.id]);
    },
    [value, onChange]
  );

  const removeOne = useCallback(
    (id) => {
      setSelectedItems((prev) => prev.filter((p) => p.id !== id));
      onChange((value || []).filter((vid) => vid !== id));
    },
    [value, onChange]
  );

  const clearAll = useCallback(() => {
    setSelectedItems([]);
    onChange([]);
  }, [onChange]);

  const overLimit = (value || []).length >= CUSTOM_TARGET_LIMIT;

  return (
    <div className="customer-picker">
      {/* 選択済みエリア */}
      <Segment>
        <Header as="h5" style={{ marginTop: 0 }}>
          選択中: {(value || []).length} / {CUSTOM_TARGET_LIMIT}名
          {selectedItems.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              style={{
                marginLeft: 12,
                fontSize: 12,
                color: "#888",
                cursor: "pointer",
                background: "transparent",
                border: "none",
                padding: 0,
                textDecoration: "underline",
              }}
            >
              すべて解除
            </button>
          )}
        </Header>
        {selectedItems.length === 0 ? (
          <p style={{ color: "#888", margin: 0 }}>
            下の検索ボックスから顧客を選択してください。
          </p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {selectedItems.map((it) => (
              <Label
                key={it.id}
                style={{ cursor: "pointer" }}
                onClick={() => removeOne(it.id)}
              >
                {it.display_name}
                <Icon name="delete" />
              </Label>
            ))}
          </div>
        )}
        {overLimit && (
          <Message warning style={{ marginTop: 10 }}>
            選択上限 ({CUSTOM_TARGET_LIMIT} 名) に達しました。これ以上追加するには、
            一度選択を解除するか「店舗で絞り込み」「全顧客」に切り替えてください。
          </Message>
        )}
      </Segment>

      {/* 検索エリア */}
      <Segment>
        <Input
          fluid
          icon="search"
          placeholder="氏名 / かな / メール / 会員コード / 電話で検索"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {searching && (
          <Loader active inline="centered" style={{ marginTop: 12 }}>
            検索中...
          </Loader>
        )}
        {searchError && (
          <Message error style={{ marginTop: 10 }}>
            {searchError}
          </Message>
        )}
        {!searching && !searchError && query.trim() && results.length === 0 && (
          <Message info style={{ marginTop: 10 }}>
            一致する顧客が見つかりません。
          </Message>
        )}
        {!searching && results.length > 0 && (
          <div className="customer-picker-results">
            {results.map((item) => {
              const sel = isSelected(item.id);
              return (
                <div
                  key={item.id}
                  className={`customer-picker-row ${sel ? "selected" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => (sel ? removeOne(item.id) : addOne(item))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      sel ? removeOne(item.id) : addOne(item);
                  }}
                  style={{
                    cursor: overLimit && !sel ? "not-allowed" : "pointer",
                    opacity: overLimit && !sel ? 0.5 : 1,
                  }}
                >
                  <div>
                    <div className="customer-picker-name">{item.display_name}</div>
                    <div className="customer-picker-meta">
                      {[item.kana, item.email, item.customer_code]
                        .filter(Boolean)
                        .join(" / ")}
                    </div>
                  </div>
                  <Icon name={sel ? "check circle" : "plus circle outline"} />
                </div>
              );
            })}
          </div>
        )}
      </Segment>
    </div>
  );
}

export default CustomerPicker;
